import React, { useEffect, useState } from "react";
import BaseDialog from "../../components/Common/BaseDialog";
import Box from "@material-ui/core/Box";
import { useSnackbar } from "notistack";
import { useDropzone } from "reactcoregk/hooks";
import { makeStyles } from "@material-ui/core/styles";
import { postData } from "../../helpers/utils";
import { API_URL } from "../../config";
import Title from "../../components/Common/Title";
import Typography from "@material-ui/core/Typography";
import HiDrop from "../../components/HiDrop";
import ErrorBoundary from "../../components/ErrorBoundary";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import GetAppIcon from "@material-ui/icons/GetApp";

const useStyles = makeStyles({
  link: {
    fontSize: 14,
    display: "flex",
    alignItems: "center",
  },
});

const readFileText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });

// Single column CSV: first cell of each line, header row excluded
const parseCveIds = (text) => {
  const ids = text
    .split(/\r?\n/)
    .map((line) => line.split(",")[0].replace(/^"+|"+$/g, "").trim().toUpperCase())
    .filter(Boolean)
    .filter((value) => value !== "CVE");
  return Array.from(new Set(ids));
};

const UploadAlertCvesDialog = (props) => {
  const { open, handleClose, alert, onUploaded } = props;

  const classes = useStyles();

  const [inProgress, setInProgress] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const { handleAcceptedFiles, setSelectedFiles, selectedFiles } =
    useDropzone();

  useEffect(() => {
    if (open) {
      setSelectedFiles([]);
    }
  }, [open, setSelectedFiles]);

  const handleTemplateDownload = () => {
    const blob = new Blob(["CVE\n"], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "CVE Upload Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    const file = selectedFiles[0];
    if (!file) {
      enqueueSnackbar("Please select a CSV file to upload", {
        variant: "warning",
      });
      return;
    }
    setInProgress(true);
    try {
      const text = await readFileText(file);
      const parsedIds = parseCveIds(text);

      const formData = new FormData();
      formData.append("file", file);
      const result = await postData(
        `${API_URL}/api/alerts/${alert.id}/cves`,
        formData
      );

      const invalidCves = result.invalidCves || [];
      const notInThreatDb = result.notInThreatDb || [];
      const rejected = new Set(
        [...invalidCves, ...notInThreatDb].map((id) => id.toUpperCase())
      );
      const validIds = parsedIds.filter((id) => !rejected.has(id));

      enqueueSnackbar(
        `CVEs uploaded: ${result.added} added, ${result.alreadyPresent} already present`,
        { variant: "success", autoHideDuration: 5000 }
      );
      if (invalidCves.length) {
        enqueueSnackbar(`Invalid CVEs skipped: ${invalidCves.join(", ")}`, {
          variant: "warning",
          autoHideDuration: 8000,
        });
      }
      if (notInThreatDb.length) {
        enqueueSnackbar(
          `Not found in Threat DB: ${notInThreatDb.join(", ")}`,
          { variant: "warning", autoHideDuration: 8000 }
        );
      }

      onUploaded(validIds);
      handleClose();
    } catch (ex) {
      enqueueSnackbar(ex.message, { variant: "error", autoHideDuration: 5000 });
    } finally {
      setInProgress(false);
    }
  };

  return (
    <BaseDialog
      form={{ id: "upload-alert-cves" }}
      title={"Upload CVEs"}
      showProgress
      maxWidth={"sm"}
      primaryAction={handleUpload}
      primaryActionTitle={"Upload"}
      open={open}
      inProgress={inProgress}
      onClose={handleClose}
    >
      <ErrorBoundary>
        <Grid container direction={"column"} spacing={1}>
          <Grid item>
            <Title>Add CVEs to the alert from csv</Title>
          </Grid>
          <Box mt={2} />
          <Grid item>
            <Grid container spacing={2} alignItems={"center"}>
              <Grid item>
                <Typography
                  color={"textSecondary"}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  Upload CSV{" "}
                </Typography>
              </Grid>
              <Grid item>
                <Divider orientation={"vertical"} style={{ height: 14 }} />
              </Grid>
              <Grid item>
                <Button
                  color={"primary"}
                  size={"small"}
                  className={classes.link}
                  onClick={handleTemplateDownload}
                >
                  <GetAppIcon /> Download template
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <div style={{ height: 250 }}>
            <HiDrop
              selectedFiles={selectedFiles}
              handleAcceptedFiles={handleAcceptedFiles}
              title={"Drag and drop CSV"}
              setSelectedFiles={setSelectedFiles}
              link={"Upload file"}
              accept=".csv, application/vnd.ms-excel, text/csv"
            />
          </div>
        </Grid>
      </ErrorBoundary>
    </BaseDialog>
  );
};

export default UploadAlertCvesDialog;
